// src/pages/RestaurantOwner/BillingSettings.jsx - WITH OPTIONAL SERVICE CHARGE

import React, { useState, useEffect } from 'react';
import { Save, Loader, FileText, ArrowLeft, Lock } from 'lucide-react';
import StorageService from '../../services/StorageService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';

const BillingSettings = ({ restaurantId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    address: '',
    phone: '',
    email: '',
    invoiceCounter: 1,
    cgstPercentage: 0,
    sgstPercentage: 0,
    serviceChargePercentage: 0,
    isGSTRegistered: false,
    chargeServiceFee: false // ✅ NEW: Toggle for service charge
  });
  const [gstNo, setGstNo] = useState('Not Set');

  useEffect(() => {
    loadSettings();
  }, [restaurantId]);

  const loadSettings = async () => {
    try {
      const restaurant = await StorageService.getRestaurant(restaurantId);
       
      
      if (restaurant) {
        setFormData({
          address: restaurant.address || '',
          phone: restaurant.phone || '',
          email: restaurant.email || '',
          invoiceCounter: restaurant.invoiceCounter || 1,
          cgstPercentage: restaurant.cgstPercentage ?? 0,
          sgstPercentage: restaurant.sgstPercentage ?? 0,
          serviceChargePercentage: restaurant.serviceChargePercentage ?? 0,
          isGSTRegistered: restaurant.isGSTRegistered ?? false,
          chargeServiceFee: restaurant.chargeServiceFee ?? false // ✅ Load service charge setting
        });
        
        const fetchedGst = restaurant.gstNo || restaurant.gstin || 'Not Set';
         
        setGstNo(fetchedGst);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGSTToggle = (isRegistered) => {
    // Check if trying to enable GST without GSTIN
    if (isRegistered && (!gstNo || gstNo === 'Not Set')) {
      alert('⚠️ Please add your GSTIN (GST Number) in Settings before enabling GST registration.');
      return;
    }
    setFormData(prev => ({
      ...prev,
      isGSTRegistered: isRegistered,
      cgstPercentage: isRegistered ? 2.5 : 0,
      sgstPercentage: isRegistered ? 2.5 : 0
      // ✅ REMOVED: Auto-setting serviceChargePercentage
    }));
  };

  // ✅ NEW: Handle Service Charge Toggle
  const handleServiceChargeToggle = (shouldCharge) => {
    setFormData(prev => ({
      ...prev,
      chargeServiceFee: shouldCharge,
      serviceChargePercentage: shouldCharge ? 5 : 0 // Default to 5% when enabled
    }));
  };

  // ✅ NEW: Handle Service Charge Percentage Change
  const handleServiceChargePercentageChange = (value) => {
    const percentage = parseFloat(value) || 0;
    if (percentage < 0 || percentage > 100) {
      alert('Service charge must be between 0% and 100%');
      return;
    }
    setFormData(prev => ({
      ...prev,
      serviceChargePercentage: percentage
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.invoiceCounter < 1) {
      alert('Invoice counter must be at least 1');
      return;
    }

    // ✅ Validation: If service charge is enabled, percentage must be > 0
    if (formData.chargeServiceFee && formData.serviceChargePercentage <= 0) {
      alert('Please enter a valid service charge percentage');
      return;
    }
    
    try {
      setSaving(true);
      
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      await updateDoc(restaurantRef, {
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        invoiceCounter: parseInt(formData.invoiceCounter),
        cgstPercentage: formData.cgstPercentage,
        sgstPercentage: formData.sgstPercentage,
        serviceChargePercentage: formData.chargeServiceFee ? formData.serviceChargePercentage : 0, // ✅ Only save if enabled
        isGSTRegistered: formData.isGSTRegistered,
        chargeServiceFee: formData.chargeServiceFee, // ✅ Save service charge toggle
        updatedAt: new Date().toISOString()
      });
      
      StorageService.clearCache();
      
      alert('✅ Billing settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading billing settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-4 mb-6">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Orders</span>
          </button>
        )}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
          Billing Settings
        </h2>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-4 md:p-6 max-w-3xl space-y-6">
        
        {/* GST CONFIGURATION SECTION */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            GST & Tax Configuration
          </h3>
          
          <div className="space-y-4">
            {/* GST Registration Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                GST Registration Status
              </label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handleGSTToggle(false)}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-semibold ${
                    !formData.isGSTRegistered
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                  disabled={saving}
                >
                  Not Registered
                </button>
                <button
                  type="button"
                  onClick={() => handleGSTToggle(true)}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-semibold ${
                    formData.isGSTRegistered
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                  disabled={saving}
                >
                  GST Registered
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                {formData.isGSTRegistered
                  ? 'CGST (2.5%) and SGST (2.5%) will be applied to invoices'
                  : 'No GST will be applied to invoices'}
              </p>
            </div>

            {/* GST Tax Rate Display */}
            {formData.isGSTRegistered && (
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <p className="text-sm text-gray-700 mb-2 font-semibold">GST Tax Rates:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-green-50 p-2 rounded">
                    <p className="text-gray-600">CGST</p>
                    <p className="text-lg font-bold text-green-600">{formData.cgstPercentage}%</p>
                  </div>
                  <div className="bg-green-50 p-2 rounded">
                    <p className="text-gray-600">SGST</p>
                    <p className="text-lg font-bold text-green-600">{formData.sgstPercentage}%</p>
                  </div>
                </div>
              </div>
            )}

            {!formData.isGSTRegistered && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  No GST will be charged on invoices. Bills will show only the item prices.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ✅ SERVICE CHARGE SECTION */}
        <div className="bg-purple-50 border-l-4 border-purple-500 rounded-lg p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-500" />
            Service Charge Configuration
          </h3>
          
          <div className="space-y-4">
            {/* Service Charge Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Do you charge Service Fee?
              </label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handleServiceChargeToggle(false)}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-semibold ${
                    !formData.chargeServiceFee
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                  disabled={saving}
                >
                  No Service Charge
                </button>
                <button
                  type="button"
                  onClick={() => handleServiceChargeToggle(true)}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-semibold ${
                    formData.chargeServiceFee
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                  disabled={saving}
                >
                  Charge Service Fee
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                {formData.chargeServiceFee
                  ? 'Service charge will be added to all invoices'
                  : 'No service charge will be applied'}
              </p>
            </div>

            {/* Service Charge Percentage Input */}
            {formData.chargeServiceFee && (
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Charge Percentage *
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.serviceChargePercentage}
                    onChange={(e) => handleServiceChargePercentageChange(e.target.value)}
                    className="flex-1 px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg font-semibold"
                    required
                    disabled={saving}
                  />
                  <span className="text-2xl font-bold text-purple-600">%</span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Typical service charge ranges from 5% to 10%
                </p>
                
                {/* Preview */}
                <div className="mt-3 bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Preview on ₹1000 bill:</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Service Charge ({formData.serviceChargePercentage}%)</span>
                    <span className="font-bold text-purple-600">
                      ₹{((1000 * formData.serviceChargePercentage) / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {!formData.chargeServiceFee && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  No service charge will be added to customer bills.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ✅ SUMMARY SECTION */}
        {(formData.isGSTRegistered || formData.chargeServiceFee) && (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-4">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Billing Summary</h3>
            <p className="text-sm text-gray-600 mb-3">Charges that will be applied on invoices:</p>
            <div className="space-y-2">
              {formData.isGSTRegistered && (
                <>
                  <div className="flex justify-between items-center bg-white p-2 rounded">
                    <span className="text-sm font-medium text-gray-700">CGST</span>
                    <span className="text-sm font-bold text-green-600">{formData.cgstPercentage}%</span>
                  </div>
                  <div className="flex justify-between items-center bg-white p-2 rounded">
                    <span className="text-sm font-medium text-gray-700">SGST</span>
                    <span className="text-sm font-bold text-green-600">{formData.sgstPercentage}%</span>
                  </div>
                </>
              )}
              {formData.chargeServiceFee && (
                <div className="flex justify-between items-center bg-white p-2 rounded">
                  <span className="text-sm font-medium text-gray-700">Service Charge</span>
                  <span className="text-sm font-bold text-purple-600">{formData.serviceChargePercentage}%</span>
                </div>
              )}
              <div className="border-t-2 border-orange-300 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-800">Total Additional Charges</span>
                  <span className="text-lg font-bold text-orange-600">
                    {(formData.cgstPercentage + formData.sgstPercentage + 
                      (formData.chargeServiceFee ? formData.serviceChargePercentage : 0)).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Counter Section */}
        <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500" />
            Invoice Numbering
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Next Invoice Number *
            </label>
            <input
              type="number"
              min="1"
              value={formData.invoiceCounter}
              onChange={(e) => setFormData({ ...formData, invoiceCounter: e.target.value })}
              className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg font-mono"
              required
              disabled={saving}
            />
            <p className="text-xs text-gray-600 mt-2">
              Current value: <strong>{formData.invoiceCounter}</strong>
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Next invoice will be: <strong className="text-orange-600">{String(formData.invoiceCounter).padStart(5, '0')}</strong>
            </p>
          </div>
        </div>

        {/* Restaurant Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Restaurant Information</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Restaurant Full Address *
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                rows="3"
                placeholder="Enter complete address..."
                required
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billing Mobile Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="+91 9876543210"
                required
                disabled={saving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                GSTIN (GST Number)
                <Lock className="w-4 h-4 text-gray-400" />
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={gstNo}
                  readOnly
                  disabled
                  className="w-full px-4 py-3 border-2 border-gray-200 bg-gray-50 rounded-lg font-mono text-gray-700 cursor-not-allowed"
                />
              </div>
              <p className="text-xs text-blue-600 mt-2 flex items-start gap-1">
                <span>ℹ️</span>
                <span>GST number cannot be changed here, please modify in Settings</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email (Optional)
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="restaurant@example.com"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          type="submit"
          disabled={saving}
          className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
        >
          {saving ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Billing Settings
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default BillingSettings;